import { config } from '@/config/env';

const DEBUG_FLAG_KEY = '__sa_ui_debug__';
const MAX_DEPTH = 5;
const MAX_ARRAY_LENGTH = 50;
const SENSITIVE_KEYS = new Set([
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'secret',
  'device_secret',
  'api_key',
  'apikey',
  'password',
  'jwt',
  'session',
  'session_id',
]);

type LoggerMethod = 'log' | 'info' | 'warn' | 'error';

type RedactedValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, unknown>
  | RedactedValue[];

const isBrowser = () => typeof window !== 'undefined';

export const setUiDebugEnabled = (enabled: boolean) => {
  if (!isBrowser()) return;
  (window as unknown as Record<string, unknown>)[DEBUG_FLAG_KEY] = enabled;
};

export const isDebugLoggingEnabled = () => {
  if (!config.isDevelopment) return false;
  if (!isBrowser()) return false;
  return Boolean(
    (window as unknown as Record<string, unknown>)[DEBUG_FLAG_KEY]
  );
};

const isErrorLike = (value: unknown): value is Error =>
  value instanceof Error ||
  (typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    'name' in value);

const redactValue = (value: unknown, depth = 0): RedactedValue => {
  if (depth > MAX_DEPTH) return '[redacted]';
  if (value == null) return value as null | undefined;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (isErrorLike(value)) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => redactValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.has(key.toLowerCase())) {
        result[key] = '***';
      } else {
        result[key] = redactValue(entry, depth + 1);
      }
    }
    return result;
  }
  return String(value);
};

const sanitizeArgs = (args: unknown[]) =>
  args.map((arg) => (typeof arg === 'object' ? redactValue(arg) : arg));

const logInternal = (method: LoggerMethod, debugOnly: boolean, args: unknown[]) => {
  if (debugOnly && !isDebugLoggingEnabled()) return;
  const logger = console[method] ?? console.log;
  logger(...sanitizeArgs(args));
};

export const debugLog = (...args: unknown[]) => {
  logInternal('log', true, args);
};

export const debugInfo = (...args: unknown[]) => {
  logInternal('info', true, args);
};

export const debugWarn = (...args: unknown[]) => {
  logInternal('warn', true, args);
};

export const logWarn = (...args: unknown[]) => {
  logInternal('warn', false, args);
};

export const logError = (...args: unknown[]) => {
  logInternal('error', false, args);
};

export const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(redactValue(value), null, 2);
  } catch (err) {
    if (err instanceof Error) {
      return `"[unserializable] ${err.message}"`;
    }
    return '"[unserializable]"';
  }
};
